import { useTranslation } from 'react-i18next';
import { BackLink } from '../components/ui';
import { setUiLanguage } from '../i18n';
import { UI_LANGS, type UiLang, updateSettings, useSettings } from '../lib/settings';
import { LANG_ENDONYMS, LANGS } from '@polyglot/engine';
import { useEffect, useState } from 'react';
import { loadLexicon } from '../lib/lexicons';

export function SettingsScreen() {
  const { t } = useTranslation();
  const s = useSettings();
  return (
    <main className="page settings">
      <BackLink />
      <h1>{t('settings.title')}</h1>
      <label className="field">
        <span>{t('settings.uiLanguage')}</span>
        <select
          value={s.uiLang}
          onChange={(e) => {
            const v = e.target.value as UiLang;
            updateSettings({ uiLang: v });
            void setUiLanguage(v);
          }}
        >
          {UI_LANGS.map((l) => (
            <option key={l} value={l}>
              {LANG_ENDONYMS[l]}
            </option>
          ))}
        </select>
      </label>
      <p className="muted small">{t('settings.uiLanguageNote')}</p>
      <label className="row toggle">
        <input
          type="checkbox"
          checked={s.sound}
          onChange={(e) => updateSettings({ sound: e.target.checked })}
        />
        {t('settings.sound')}
      </label>
      <label className="row toggle">
        <input
          type="checkbox"
          checked={s.haptics}
          onChange={(e) => updateSettings({ haptics: e.target.checked })}
        />
        {t('settings.haptics')}
      </label>
      <label className="field">
        <span>{t('settings.motion')}</span>
        <select
          value={s.motion}
          onChange={(e) =>
            updateSettings({ motion: e.target.value as 'system' | 'reduced' | 'full' })
          }
        >
          <option value="system">{t('settings.motionSystem')}</option>
          <option value="reduced">{t('settings.motionReduced')}</option>
          <option value="full">{t('settings.motionFull')}</option>
        </select>
      </label>
      <label className="row toggle">
        <input
          type="checkbox"
          checked={s.relaxHints}
          onChange={(e) => updateSettings({ relaxHints: e.target.checked })}
        />
        {t('settings.relaxHints')}
      </label>
      <OfflineDictionaries />
    </main>
  );
}

/** Downloads dictionaries through the service worker cache so solo play works offline. */
function OfflineDictionaries() {
  const { t } = useTranslation();
  const [cached, setCached] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const refresh = async () => {
    try {
      const cache = await caches.open('lexicons-v1');
      const keys = (await cache.keys()).map((r) => new URL(r.url).pathname);
      setCached(
        Object.fromEntries(
          LANGS.map((l) => [
            l,
            keys.some((k) => k.startsWith(`/lexicons/${l}/`) && k.includes('/words-')),
          ]),
        ),
      );
    } catch {
      setCached({});
    }
  };
  useEffect(() => {
    void refresh();
  }, []);
  return (
    <section className="card">
      <h2>{t('offline.title')}</h2>
      <p className="muted small">{t('offline.explain')}</p>
      {LANGS.map((l) => (
        <div key={l} className="row round-row">
          <span>{LANG_ENDONYMS[l]}</span>
          {cached[l] ? (
            <span className="badge ok">{t('offline.ready')}</span>
          ) : (
            <button
              className="btn small ghost"
              disabled={busy !== null}
              onClick={async () => {
                setBusy(l);
                try {
                  await loadLexicon(l);
                } finally {
                  setBusy(null);
                  await refresh();
                }
              }}
            >
              {busy === l ? '…' : t('offline.download')}
            </button>
          )}
        </div>
      ))}
    </section>
  );
}
